import React, {
  useState, useMemo, useCallback, useEffect, memo
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Popper from '@material-ui/core/Popper';
import Fade from '@material-ui/core/Fade';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Dialog from '@material-ui/core/Dialog';
import RoundedButton from 'components/MuiComponents/RoundedButton';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import { mediaMUI } from 'theme/styled-theme';
import IconButton from 'components/MuiComponents/IconButton';
import {
  ClearIcon,
  AddIcon,
  RemoveIcon,
  KeyboardArrowUpIcon,
  KeyboardArrowDownIcon,
} from 'components/StyledIcons/StyledIcons';
import { sendEvent } from 'utils/gtag';

const Root = styled.div`
  cursor: pointer;
  background-color: #ffffff;
  border-radius: 2px;
  border: 1px solid ${props => props.isActive ? '#00b33b' : '#dbdbdb'};
  height: ${props => props.navigation ? '2.625rem' : '3rem'};
  padding: 0.875rem 1rem;
  width: 100%;
  position: relative;
  display: flex;
`;

const DropdownFeild = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Dropdown = styled.div`
  background: #FFFFFF;
  box-shadow: 1px 4px 7px 0 rgba(0,0,0,0.28);
  border-radius: 4px;
  width: ${props => props.width ? `${props.width}px` : '100%'};
  margin-top: 0.25rem;
  max-width: 360px;
  padding: 1.5rem 1rem;
`;

const GuestsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
`;

const GuestsListItem = styled.li`
  display: flex;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h4`
  margin-bottom: 0;
  font-size: 1rem;
  color: #595959;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #828183;
`;

const EmptyDescription = styled.div`
  width: 100%;
  height: 1.375rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
`;

const GuestNumber = styled.span`
  font-size: 1rem;
  color: #595959;
  font-weight: 700;
  margin: 0 0.75rem;
`;

const TotalGuests = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
`;

const DialogActions = styled(MuiDialogActions)`
  && {
    border-bottom: 1px solid #b4b4b4;
  }
`;

const DialogContent = styled(MuiDialogContent)`
  && {
    background-color: white;
    padding-top: 24px;
    padding-bottom: 24px;
  }
`;

const GuestPickerList = memo(({
  /* eslint-disable react/prop-types */
  totalGuests,
  onAdultMinus,
  onAdultAdd,
  adult,
  onChildrenMinus,
  onChildrenAdd,
  childrenValue: _children,
  onInfantMinus,
  onInfantAdd,
  infant,
  showWarning,
  maximumSizeWarning,
  t
  /* eslint-enable react/prop-types */
}) => {
  const iconStyle = useMemo(() => ({
    root: {
      fontSize: 12,
    }
  }), []);

  const iconButtonStyle = useMemo(() => ({
    root: {
      border: '1px solid #00B33B',
    }
  }), []);

  return (
    <>
      <TotalGuests>{`Total guest: ${totalGuests}`}</TotalGuests>
      <GuestsList>
        <GuestsListItem>
          <Content>
            <Title>{t('COMMON.ADULT')}</Title>
            <EmptyDescription />
          </Content>
          <Actions>
            <IconButton
              onClick={onAdultMinus}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-adult-minus"
            >
              <RemoveIcon overwritestyles={iconStyle} />
            </IconButton>
            <GuestNumber>{adult}</GuestNumber>
            <IconButton
              onClick={onAdultAdd}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-adult-add"
            >
              <AddIcon overwritestyles={iconStyle} />
            </IconButton>
          </Actions>
        </GuestsListItem>

        <GuestsListItem>
          <Content>
            <Title>{t('COMMON.CHILDREN')}</Title>
            <EmptyDescription />
          </Content>
          <Actions>
            <IconButton
              onClick={onChildrenMinus}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-children-minus"
            >
              <RemoveIcon overwritestyles={iconStyle} />
            </IconButton>
            <GuestNumber>{_children}</GuestNumber>
            <IconButton
              onClick={onChildrenAdd}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-children-add"
            >
              <AddIcon overwritestyles={iconStyle} />
            </IconButton>
          </Actions>
        </GuestsListItem>

        <GuestsListItem>
          <Content>
            <Title>{t('COMMON.INFANT')}</Title>
            <Description>{t('COMMON.INFANT_SEATS')}</Description>
          </Content>
          <Actions>
            <IconButton
              onClick={onInfantMinus}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-infant-minus"
            >
              <RemoveIcon overwritestyles={iconStyle} />
            </IconButton>
            <GuestNumber>{infant}</GuestNumber>
            <IconButton
              onClick={onInfantAdd}
              overwritestyles={iconButtonStyle}
              data-cy="cy-passenger-infant-add"
            >
              <AddIcon overwritestyles={iconStyle} />
            </IconButton>
          </Actions>
        </GuestsListItem>
      </GuestsList>
      { showWarning ? maximumSizeWarning : '' }
    </>
  );
});

const GuestsPicker = ({
  onSave,
  adultNumber,
  childrenNumber,
  infantNumber,
  maximumGuests,
  maximumSizeWarning: _maximumSizeWarning,
}) => {
  const { t } = useTranslation(['new-version']);
  const [adult, setAdult] = useState(adultNumber);
  const [children, setChildren] = useState(childrenNumber);
  const [infant, setInfant] = useState(infantNumber);
  const [totalGuests, setTotalGuests] = useState(adultNumber + childrenNumber + infantNumber);
  const [dropdownWidth, setDropdownWidth] = useState(320);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [onClickAway, setOnClickAway] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const maximumSizeWarning = _maximumSizeWarning || t('COMMON.MAXIMUM_GUESTS_OF_CARTYPE', { maximum: maximumGuests });

  const open = Boolean(anchorEl);

  const popperConfig = useMemo(() => ({
    flip: {
      enabled: true,
    },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'scrollParent',
      padding: 0,
    },
    arrow: {
      enabled: false,
    },
  }), []);

  const dialogConfirmButtonStyle = useMemo(() => ({
    root: {
      color: '#fff',
      backgroundColor: '#00B33B',
      height: '48px',
      fontWeight: '400',
      marginBottom: '0.5rem'
    }
  }));

  const onClickHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      if (!onClickAway) {
        setAnchorEl(anchorEl ? null : e.currentTarget);
        setDropdownWidth(dropdownWidth || e.currentTarget.offsetWidth);
      } else {
        setOnClickAway(false);
      }

      setDialogOpen(true);
    },
    [anchorEl, onClickAway],
  );

  const handleDialogClose = useCallback(
    e => {
      e.stopPropagation();
      setDialogOpen(false);
      setAnchorEl(null);
      onSave({ adult, children, infant });
    },
    [adult, children, infant],
  );

  const handleClickAway = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();

    // this is a hack to prevent handleClickAway and onClickHandler happen a the same time
    setOnClickAway(true);
    setTimeout(() => setAnchorEl(null), 0);
    setTimeout(() => setOnClickAway(false), 500);
    // end of hack

    onSave({ adult, children, infant });
    sendEvent({ action: 'Close Guestpicker', category: 'engagement' });
  }, [adult, children, infant]);

  const onAdultAdd = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();

    if (totalGuests === maximumGuests && !showWarning) setShowWarning(true);
    if (adult < maximumGuests && totalGuests < maximumGuests) setAdult(adult + 1);
  }, [adult, totalGuests, showWarning]);

  const onAdultMinus = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();

    if (adult > 0 && totalGuests > 1) setAdult(adult - 1);
    if (showWarning) setShowWarning(false);
  }, [adult, totalGuests, showWarning]);

  const onChildrenAdd = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();

    if (totalGuests === maximumGuests && !showWarning) setShowWarning(true);
    if (children < maximumGuests && totalGuests < maximumGuests) setChildren(children + 1);
  }, [children, totalGuests, showWarning]);

  const onChildrenMinus = useCallback(e => {
    e.stopPropagation();
    if (children > 0 && totalGuests > 1) setChildren(children - 1);
    if (showWarning) setShowWarning(false);
  }, [children, totalGuests, showWarning]);

  const onInfantAdd = useCallback(e => {
    e.stopPropagation();
    if (totalGuests === maximumGuests && !showWarning) setShowWarning(true);
    if (infant < maximumGuests && totalGuests < maximumGuests) setInfant(infant + 1);
  }, [infant, totalGuests, showWarning]);

  const onInfantMinus = useCallback(e => {
    e.stopPropagation();
    if (infant > 0 && totalGuests > 1) setInfant(infant - 1);
    if (showWarning) setShowWarning(false);
  }, [infant, totalGuests, showWarning]);

  useEffect(() => {
    setTotalGuests(infant + adult + children);
  }, [infant, adult, children]);

  const matchePhone = useMediaQuery(mediaMUI.forPhoneOnly);

  return (
    <Root
      data-cy="cy-passenger-select"
      data-testid="searchbox"
      isActive={open}
      onClick={onClickHandler}
    >
      <DropdownFeild>
        <span data-cy="cy-totalguests">{`${totalGuests} guests`}</span>
        { open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon /> }
      </DropdownFeild>
      {
        matchePhone
          ? (
            <Dialog
              key="all-filters-dialog"
              data-cy="cy-all-filters-dialog"
              open={dialogOpen}
              onClose={handleDialogClose}
              fullScreen
            >
              <DialogActions key="all-filters-dialog-action-top">
                <IconButton
                  data-cy="cy-close-filters-dialog"
                  onClick={handleDialogClose}
                >
                  <ClearIcon />
                </IconButton>
              </DialogActions>
              <DialogContent key="all-filters-dialog-content">
                <GuestPickerList
                  totalGuests={totalGuests}
                  onAdultMinus={onAdultMinus}
                  onAdultAdd={onAdultAdd}
                  adult={adult}
                  onChildrenMinus={onChildrenMinus}
                  onChildrenAdd={onChildrenAdd}
                  childrenValue={children}
                  onInfantMinus={onInfantMinus}
                  onInfantAdd={onInfantAdd}
                  infant={infant}
                  showWarning={showWarning}
                  maximumSizeWarning={maximumSizeWarning}
                  t={t}
                />
              </DialogContent>
              <DialogActions key="all-filters-dialog-action-bottom">
                <RoundedButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  overwritestyles={dialogConfirmButtonStyle}
                  onClick={handleDialogClose}
                >
                  Save
                </RoundedButton>
              </DialogActions>
            </Dialog>
          )
          : (
            <Popper
              placement="bottom-start"
              open={open}
              anchorEl={anchorEl}
              modifiers={popperConfig}
              transition
              style={{ zIndex: 1302 }}
            >
              {({ TransitionProps }) => (
                <ClickAwayListener onClickAway={handleClickAway}>
                  <Fade {...TransitionProps} timeout={150}>
                    <Dropdown width={dropdownWidth} data-cy="guestpicker-dropdown" data-testid="guestpicker-dropdown">
                      <GuestPickerList
                        totalGuests={totalGuests}
                        onAdultMinus={onAdultMinus}
                        onAdultAdd={onAdultAdd}
                        adult={adult}
                        onChildrenMinus={onChildrenMinus}
                        onChildrenAdd={onChildrenAdd}
                        childrenValue={children}
                        onInfantMinus={onInfantMinus}
                        onInfantAdd={onInfantAdd}
                        infant={infant}
                        showWarning={showWarning}
                        maximumSizeWarning={maximumSizeWarning}
                        t={t}
                      />
                    </Dropdown>
                  </Fade>
                </ClickAwayListener>
              )}
            </Popper>
          )
      }

    </Root>
  );
};

GuestsPicker.propTypes = {
  onSave: PropTypes.func,
  adultNumber: PropTypes.number,
  childrenNumber: PropTypes.number,
  infantNumber: PropTypes.number,
  maximumGuests: PropTypes.number,
  maximumSizeWarning: PropTypes.string,
};

GuestsPicker.defaultProps = {
  onSave: () => {},
  adultNumber: 1,
  childrenNumber: 0,
  infantNumber: 0,
  maximumGuests: 8,
  maximumSizeWarning: '',
};

export default GuestsPicker;
